import React, { useState } from 'react';
import CustomSelect from '../components/CustomSelect';

const DATA_TYPE_LABELS = {
  boolean: 'Checkbox',
  enum: 'Named Scale',
  number: 'Numeric Scale'
};

const SYSTEM_FACTORS = [
  { key: 'friend_requests', label: 'Friend Requests', description: 'Try to place friends together in the same class.' },
  { key: 'keep_away', label: 'Keep Away', description: 'Separate students who should not be in the same class.' },
  { key: 'gender_balance', label: 'Gender Balance', description: 'Distribute genders evenly across classes.' },
  { key: 'behavior_balance', label: 'Behavior Balance', description: 'Spread behavior-flagged students across classes.' },
  { key: 'academic_balance', label: 'Academic Balance', description: 'Balance academic score averages across classes.' }
];

export default function FactorsView({
  factorsWorkspace,
  isLoading,
  isSaving,
  saveError,
  onCreateFactor,
  onUpdateFactor,
  onDeleteFactor,
  onUpdateFactorSetting,
  terminology,
  permissions
}) {
  const canWrite = (permissions ?? []).includes('factors.write');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingFactor, setEditingFactor] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [deletingFactor, setDeletingFactor] = useState(null);

  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formDataType, setFormDataType] = useState('boolean');
  const [formChoices, setFormChoices] = useState(['', '']);
  const [formMin, setFormMin] = useState('1');
  const [formMax, setFormMax] = useState('5');
  const [formError, setFormError] = useState('');

  if (isLoading || !factorsWorkspace) {
    return (
      <section className="panel roster-loading">
        <div className="loading-card">
          <div className="spinner-ring" />
          <h1>Loading factors</h1>
          <p>Preparing the factor definitions workspace.</p>
        </div>
      </section>
    );
  }

  const definitions = factorsWorkspace.factorDefinitions ?? [];
  const factorSettings = factorsWorkspace.factorSettings ?? [];

  function resetForm() {
    setFormName('');
    setFormDescription('');
    setFormDataType('boolean');
    setFormChoices(['', '']);
    setFormMin('1');
    setFormMax('5');
    setFormError('');
  }

  function handleOpenCreate() {
    resetForm();
    setEditingFactor(null);
    setIsCreateDialogOpen(true);
  }

  function handleOpenEdit(factor) {
    setFormName(factor.name);
    setFormDescription(factor.description ?? '');
    setFormDataType(factor.dataType);
    if (factor.dataType === 'enum') {
      setFormChoices(factor.options?.choices?.length >= 2 ? [...factor.options.choices] : ['', '']);
    } else {
      setFormChoices(['', '']);
    }
    if (factor.dataType === 'number') {
      setFormMin(String(factor.options?.min ?? 1));
      setFormMax(String(factor.options?.max ?? 5));
    } else {
      setFormMin('1');
      setFormMax('5');
    }
    setFormError('');
    setEditingFactor(factor);
    setIsCreateDialogOpen(true);
  }

  function handleCloseDialog() {
    if (isSaving) return;
    setIsCreateDialogOpen(false);
    setEditingFactor(null);
    resetForm();
  }

  function handleAddChoice() {
    setFormChoices([...formChoices, '']);
  }

  function handleRemoveChoice(index) {
    if (formChoices.length <= 2) return;
    setFormChoices(formChoices.filter((_, i) => i !== index));
  }

  function handleChangeChoice(index, value) {
    const updated = [...formChoices];
    updated[index] = value;
    setFormChoices(updated);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFormError('');

    const payload = {
      name: formName.trim(),
      description: formDescription.trim(),
      dataType: formDataType,
      options: {}
    };

    if (formDataType === 'enum') {
      payload.options.choices = formChoices.map((c) => c.trim()).filter(Boolean);
    } else if (formDataType === 'number') {
      payload.options.min = Number(formMin);
      payload.options.max = Number(formMax);
    }

    try {
      if (editingFactor) {
        await onUpdateFactor(editingFactor.id, payload);
      } else {
        await onCreateFactor(payload);
      }
      handleCloseDialog();
    } catch (error) {
      setFormError(error?.message || 'An error occurred.');
    }
  }

  function handleOpenDelete(factor) {
    setDeletingFactor(factor);
    setIsDeleteConfirmOpen(true);
  }

  async function handleConfirmDelete() {
    if (!deletingFactor) return;
    try {
      await onDeleteFactor(deletingFactor.id);
      setIsDeleteConfirmOpen(false);
      setDeletingFactor(null);
    } catch (error) {
      setFormError(error?.message || 'Unable to delete.');
    }
  }

  return (
    <div className="factors-screen">
      <section className="hero-card roster-hero">
        <div className="hero-copy">
          <div className="eyebrow-row">
            <span className="tone-pill accent">Configuration</span>
            <span className="eyebrow-copy">
              {definitions.length} custom factor{definitions.length === 1 ? '' : 's'} defined
            </span>
          </div>
          <h1>Factor Definitions</h1>
          <p>
            Define custom factors that can be tracked per {terminology.student.toLowerCase()} and used by the solver to balance {terminology.class.toLowerCase()} placements. These factors are shared across all projects in your {terminology.school.toLowerCase()}.
          </p>
        </div>

        {canWrite && (
          <div className="hero-actions">
            <button type="button" className="primary-button" onClick={handleOpenCreate}>
              + New factor
            </button>
          </div>
        )}
      </section>

      {saveError && (
        <div className="preview-bar error-bar">
          <span className="preview-pill">Factor issue</span>
          <span>{saveError}</span>
        </div>
      )}

      <div className="factors-weight-explainer">
        <strong>How weights work</strong>
        <p>
          Weights are relative to each other — a factor with weight 2.0 is twice as important as
          one with weight 1.0. Set a weight to 0 or disable the factor to ignore it entirely.
        </p>
      </div>

      <section className="panel factors-section-panel">
        <div className="panel-head">
          <div>
            <span className="panel-kicker">Built-in factors</span>
            <h2>System factors</h2>
          </div>
          <span className="toolbar-meta">Always available</span>
        </div>

        <div className="factors-grid">
          {SYSTEM_FACTORS.filter((factor) => terminology.showAcademicScore || factor.key !== 'academic_balance').map((factor) => {
            const setting = factorSettings.find((s) => s.factorKey === factor.key);
            return (
              <article key={factor.key} className="factor-card factor-card-system">
                <div className="factor-card-head">
                  <strong>{factor.label}</strong>
                  <span className="tone-pill neutral">System</span>
                </div>
                <p>{factor.description}</p>
                {factor.key === 'friend_requests' && setting && (
                  <label className="roster-checkbox factor-guarantee-option">
                    <input
                      type="checkbox"
                      checked={setting.config?.guaranteeOneMatch ?? false}
                      onChange={(event) =>
                        onUpdateFactorSetting(setting.id, {
                          config: { ...setting.config, guaranteeOneMatch: event.target.checked }
                        })
                      }
                      disabled={isSaving || !setting.isEnabled || !canWrite}
                    />
                    <span>Guarantee at least one match per {terminology.student.toLowerCase()}</span>
                  </label>
                )}
                {setting && (
                  <FactorWeightEditor
                    setting={setting}
                    onUpdate={onUpdateFactorSetting}
                    disabled={isSaving}
                    readOnly={!canWrite}
                  />
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="panel factors-section-panel">
        <div className="panel-head">
          <div>
            <span className="panel-kicker">Custom factors</span>
            <h2>{terminology.school}-defined factors</h2>
          </div>
          <span className="toolbar-meta">{definitions.length} defined</span>
        </div>

        {definitions.length === 0 ? (
          <div className="loading-card">
            <h1>No custom factors yet</h1>
            <p>Create a custom factor to start tracking additional {terminology.student.toLowerCase()} attributes.</p>
          </div>
        ) : (
          <div className="factors-grid">
            {definitions.map((factor) => {
              return (
                <article key={factor.id} className="factor-card">
                  <div className="factor-card-head">
                    <strong>{factor.name}</strong>
                    <span className={`tone-pill ${factor.isActive ? 'accent' : 'neutral'}`}>
                      {DATA_TYPE_LABELS[factor.dataType] ?? factor.dataType}
                    </span>
                  </div>
                  {factor.description && <p className="factor-card-desc">{factor.description}</p>}
                  <div className="factor-card-detail">
                    {factor.dataType === 'enum' && factor.options?.choices && (
                      <span className="factor-detail-row">
                        Values: {factor.options.choices.join(' → ')}
                      </span>
                    )}
                    {factor.dataType === 'number' && (
                      <span className="factor-detail-row">
                        Range: {factor.options?.min ?? '?'} – {factor.options?.max ?? '?'}
                      </span>
                    )}
                    {(() => {
                      const setting = factorSettings.find(
                        (s) => s.factorKey === factor.slug || s.config?.customFactorKey === factor.slug
                      );
                      return setting ? (
                        <FactorWeightEditor
                          setting={setting}
                          onUpdate={onUpdateFactorSetting}
                          disabled={isSaving}
                          readOnly={!canWrite}
                        />
                      ) : (
                        <span className="factor-detail-row">No weight configured yet</span>
                      );
                    })()}
                  </div>
                  {canWrite && (
                    <div className="factor-card-actions">
                      <button
                        type="button"
                        className="utility-button"
                        onClick={() => handleOpenEdit(factor)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="utility-button danger-text"
                        onClick={() => handleOpenDelete(factor)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>

      {isCreateDialogOpen && (
        <div className="workspace-dialog-backdrop" onClick={handleCloseDialog}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">{editingFactor ? 'Edit factor' : 'New factor'}</span>
                <h2>{editingFactor ? `Edit "${editingFactor.name}"` : 'Create a custom factor'}</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseDialog}
                disabled={isSaving}
              >
                ×
              </button>
            </div>

            {formError && <div className="auth-error">{formError}</div>}

            <form className="roster-form" onSubmit={handleSubmit}>
              <label className="auth-field">
                <span>Factor name</span>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. ELL Level, Leadership, Math Tier"
                  required
                  autoFocus
                />
              </label>

              <label className="auth-field">
                <span>Description (optional)</span>
                <input
                  type="text"
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Brief explanation of what this factor tracks"
                />
              </label>

              <label className="auth-field">
                <span>Factor type</span>
                <CustomSelect
                  value={formDataType}
                  options={[
                    { value: 'boolean', label: `Checkbox (yes/no per ${terminology.student.toLowerCase()})` },
                    { value: 'enum', label: 'Named Scale (e.g. Bad, OK, Good, Excellent)' },
                    { value: 'number', label: 'Numeric Scale (e.g. 1–5)' }
                  ]}
                  onChange={(val) => setFormDataType(val)}
                />
              </label>

              {formDataType === 'enum' && (
                <div className="factor-choices-editor">
                  <span className="auth-field-label">Scale values (in order)</span>
                  {formChoices.map((choice, index) => (
                    <div key={index} className="factor-choice-row">
                      <input
                        type="text"
                        value={choice}
                        onChange={(e) => handleChangeChoice(index, e.target.value)}
                        placeholder={`Level ${index + 1}`}
                        className="factor-choice-input"
                      />
                      {formChoices.length > 2 && (
                        <button
                          type="button"
                          className="utility-button factor-choice-remove"
                          onClick={() => handleRemoveChoice(index)}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    type="button"
                    className="utility-button factor-add-choice-btn"
                    onClick={handleAddChoice}
                  >
                    + Add level
                  </button>
                </div>
              )}

              {formDataType === 'number' && (
                <div className="factor-range-editor">
                  <label className="auth-field factor-range-field">
                    <span>Minimum</span>
                    <input
                      type="number"
                      value={formMin}
                      onChange={(e) => setFormMin(e.target.value)}
                    />
                  </label>
                  <label className="auth-field factor-range-field">
                    <span>Maximum</span>
                    <input
                      type="number"
                      value={formMax}
                      onChange={(e) => setFormMax(e.target.value)}
                    />
                  </label>
                </div>
              )}

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCloseDialog}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isSaving}>
                  {isSaving ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isSaving
                    ? (editingFactor ? 'Saving…' : 'Creating…')
                    : (editingFactor ? 'Save changes' : 'Create factor')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isDeleteConfirmOpen && deletingFactor && (
        <div className="workspace-dialog-backdrop" onClick={() => setIsDeleteConfirmOpen(false)}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">Confirm delete</span>
                <h2>Delete "{deletingFactor.name}"?</h2>
              </div>
            </div>
            <div className="delete-confirm-body">
              <p>
                This will permanently remove the <strong>{deletingFactor.name}</strong> factor
                definition. Any {terminology.student.toLowerCase()} data associated with this factor will no longer be referenced
                by the solver.
              </p>
            </div>
            <div className="roster-form-actions">
              <button
                type="button"
                className="utility-button"
                onClick={() => { setIsDeleteConfirmOpen(false); setDeletingFactor(null); }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-button factors-delete-btn"
                onClick={handleConfirmDelete}
                disabled={isSaving}
              >
                {isSaving ? 'Deleting…' : 'Delete factor'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FactorWeightEditor({ setting, onUpdate, disabled, readOnly }) {
  const [draft, setDraft] = useState(String(setting.weight));
  const [saving, setSaving] = useState(false);

  const currentWeight = String(setting.weight);

  function handleSave() {
    const value = Number(draft);
    if (!Number.isFinite(value) || value < 0) {
      setDraft(currentWeight);
      return;
    }
    if (String(value) === currentWeight) return;

    setSaving(true);
    onUpdate(setting.id, { weight: value })
      .catch(() => setDraft(currentWeight))
      .finally(() => setSaving(false));
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    } else if (e.key === 'Escape') {
      setDraft(currentWeight);
      e.target.blur();
    }
  }

  function handleToggleEnabled() {
    onUpdate(setting.id, { isEnabled: !setting.isEnabled }).catch(() => {});
  }

  return (
    <div className="factor-weight-row">
      <label className="factor-weight-toggle">
        <input
          type="checkbox"
          checked={setting.isEnabled}
          onChange={handleToggleEnabled}
          disabled={disabled || saving || readOnly}
        />
        <span>{setting.isEnabled ? 'Enabled' : 'Disabled'}</span>
      </label>
      <div className="factor-weight-input-wrap">
        <span className="factor-weight-label">Weight</span>
        <input
          type="number"
          className="factor-weight-input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
          min="0"
          step="0.1"
          disabled={disabled || saving || !setting.isEnabled || readOnly}
        />
      </div>
    </div>
  );
}
